import { useEffect, useRef } from 'react';

import { useChecklist } from 'entities/onboarding/api/useChecklist';
import { useOnboardingState } from 'entities/onboarding/api/useOnboardingState';
import type { ChecklistResponse, ChecklistStep, OnboardingStateResponse, StepStatus } from 'entities/onboarding/types';
import type { ChecklistProgress } from 'features/onboarding/lib/checklist';
import { blockingStepId, checklistProgress, currentStep, flattenSteps } from 'features/onboarding/lib/checklist';

/** Enrolled and past the wizard, so there is a checklist to fetch. */
function isEnrolled(state: OnboardingStateResponse | undefined): boolean {
  return Boolean(state?.wizard_complete) && state?.checklist_enabled !== false;
}

export type ChecklistProgressState = {
  /** Where the user is in setup, or null once there is nothing left to point at. */
  progress: ChecklistProgress | null;
  /** True until it is known which of those two this is. */
  isPending: boolean;
};

/**
 * How far through setup the user is, for callers that only need to say so — the launcher, not the
 * panel.
 *
 * Shares the checklist query with the panel and deliberately skips the signal sync, so putting this
 * on every page costs one cached request rather than a sync per render.
 *
 * `isPending` covers both requests, and stays false for anyone the checklist doesn't apply to: with
 * no enrollment the checklist query never runs, so waiting on it would wait forever.
 */
export function useChecklistProgress(): ChecklistProgressState {
  const { data: onboardingState, isPending: isStatePending } = useOnboardingState();
  const enrolled = isEnrolled(onboardingState);
  const { query } = useChecklist({ enabled: enrolled });
  return {
    // Gated on enrolment as well as data: the query keeps its cache once disabled, so a membership
    // that stops being enrolled would otherwise go on reporting the progress it had.
    progress: enrolled && query.data ? checklistProgress(query.data) : null,
    isPending: isStatePending || (enrolled && query.isPending && !query.isError),
  };
}

export type OnboardingChecklist =
  // Not enrolled, or the checklist couldn't be loaded — the panel shows the conversation alone.
  | { status: 'unavailable' }
  | { status: 'loading' }
  | {
      status: 'ready';
      checklist: ChecklistResponse;
      steps: ChecklistStep[];
      current: ChecklistStep | null;
      blockerTitle: (step: ChecklistStep) => string | null;
      /** Resolves true once the change has landed and the checklist has refetched. */
      settle: (step: ChecklistStep, status: StepStatus) => Promise<boolean>;
      /** Non-null while that step's update is in flight, so its control can spin. */
      pendingAction: (stepId: string) => StepStatus | null;
    };

/**
 * Loads the setup checklist and owns everything that isn't rendering: enrollment, the once-per-open
 * signal sync, and settling a step.
 *
 * The result is a discriminated union rather than a bag of flags, so a caller can't render a
 * spinner forever on a query that errored.
 */
export function useOnboardingChecklist(): OnboardingChecklist {
  const { data: onboardingState } = useOnboardingState();
  const enrolled = isEnrolled(onboardingState);
  const { query, updateStep, syncChecklist } = useChecklist({ enabled: enrolled });
  const { mutateAsync: sync } = syncChecklist;

  // Without this nothing ever calls /sync, so steps keyed on orders, keys or team size could only be
  // closed by hand. Failure is swallowed: every step stays manually completable regardless.
  const syncedRef = useRef(false);
  const hasChecklist = Boolean(query.data);
  useEffect(() => {
    if (syncedRef.current || !hasChecklist) {
      return;
    }
    syncedRef.current = true;
    sync().catch(() => undefined);
  }, [hasChecklist, sync]);

  if (!enrolled || query.isError) {
    return { status: 'unavailable' };
  }
  if (query.isPending || !query.data) {
    return { status: 'loading' };
  }

  const checklist = query.data;
  const steps = flattenSteps(checklist.sections);
  const titleById = new Map(steps.map((step) => [step.step_id, step.title]));
  const inFlight = updateStep.isPending ? updateStep.variables : null;

  return {
    status: 'ready',
    checklist,
    steps,
    current: currentStep(steps),
    blockerTitle: (step) => {
      const blockerId = blockingStepId(step);
      return blockerId ? (titleById.get(blockerId) ?? null) : null;
    },
    settle: (step, status) =>
      updateStep
        .mutateAsync({ stepId: step.step_id, status })
        .then(() => true)
        .catch(() => false),
    pendingAction: (stepId) => (inFlight?.stepId === stepId ? inFlight.status : null),
  };
}
